import React, { useContext } from 'react'
import styled from 'styled-components'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import TransportTimeline from './common/TransportTimeline'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLoadingBox from 'components/Luxkit/Typography/TextLoadingBox'
import { fareCabinLabelMap } from 'constants/flight'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useAirlineByCode, useAirport } from 'tripPlanner/hooks/api'
import { FlightItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import FlightCTAContent from './CTAContent/FlightCTAContent'
import Breakpoint from 'components/Common/Breakpoint'
import BookedLabel from 'tripPlanner/components/BookedLabel'

function getAirportName(
  airport: App.AirportLocation | undefined,
  placeName: string,
) {
  let airportName: string | undefined

  if (airport) {
    airportName =
      airport.airportName || airport.cityAirportName || airport.cityDesc
  }
  // Fallback if no airport or if it's missing data
  if (!airportName) {
    airportName = placeName
  }
  return airportName
}

const Logo = styled.img`
  width: 100%;
`

const TimelineContainer = styled.div`
  clear: both; /* Make sure the timeline sits below the corner buttons */
`

interface Props {
  item: FlightItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailFlightCard({ item, variant, tripNameLabel }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  // Using 'airlineCode' to reliably lookup airline images since some
  // airline names returned by api.flightstats.com doesn't always map to an airline in svc-flights
  const { data: airline } = useAirlineByCode({
    code: item.flightNumber?.slice(0, 2),
  })

  const airlineLogoUrl = airline?.airlineLogo
  const logoImg = airlineLogoUrl ? <Logo src={airlineLogoUrl} /> : undefined

  const { data: departureAirport, isLoading: isDepartureAirportLoading } =
    useAirport({
      code: item.originAirportCode,
    })

  const { data: arrivalAirport, isLoading: isArrivalAirportLoading } =
    useAirport({
      code: item.destinationAirportCode,
    })

  const departureAirportName = getAirportName(
    departureAirport,
    item.startPlace.name,
  )
  const arrivalAirportName = getAirportName(arrivalAirport, item.endPlace.name)
  const hasAirportNames = !!(departureAirportName && arrivalAirportName)
  const isLoadingAirportData =
    isDepartureAirportLoading || isArrivalAirportLoading

  const title = hasAirportNames ? (
    <>
      {departureAirportName} <LineArrowRightIcon size="S" />{' '}
      {arrivalAirportName}
    </>
  ) : isLoadingAirportData ? (
    <TextLoadingBox width={160} />
  ) : (
    'Flight'
  )

  let flightNumber = item.flightNumber
  if (flightNumber?.match(/^\d+$/) && airline?.airlineCode) {
    flightNumber = `${airline.airlineCode}${flightNumber}`
  }

  const fareClass = item.savedItemData ?
    fareCabinLabelMap[item.savedItemData.fareClass] :
    undefined

  const travellerCount: App.Occupants = item.savedItemData?.travellerCounts ?? {
    adults: 0,
  }

  const details = [
    flightNumber,
    fareClass,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <DetailCardLayout>
      <DetailCardLayout.Image>{logoImg}</DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} />}
        <VerticalSpacer gap={8}>
          {item.isBooked && <Breakpoint max="desktop"><BookedLabel /></Breakpoint>}
          <div>
            <ItemTitle icon={LinePlaneDepartureIcon}>{title}</ItemTitle>
            {details && (
              <BodyText variant="medium" colour="neutral-three">
                {details}
              </BodyText>
            )}
          </div>
          <TimelineContainer>
            <TransportTimeline
              item={item}
              originLabel={item.originAirportCode}
              destinationLabel={item.destinationAirportCode}
              variant={variant}
            />
          </TimelineContainer>
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          {isLeBookmark(item) && <FlightCTAContent item={item} variant={variant} />}
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailFlightCard
